/**
 * LOUD/QUIET Window — retro "app window" surface with a titlebar, three traffic-light dots
 * and a mono filename. A signature brand motif.
 *
 * Ported 1:1 from the compiled `_ds_bundle.js`. Structure is: bordered outer shell (shadow +
 * background) > 44px titlebar (always white, bottom border, three dots, mono title pushed
 * right by margin-left:auto) > body (own background, 24px padding).
 *
 * Non-obvious decisions:
 * - The traffic-light dots are decorative and carry no accessible name in the bundle; they are
 *   left exactly as-is (plain spans), so no aria-hidden was added that the bundle did not set.
 * - `title` is a ReactNode here, so React.HTMLAttributes' string `title` is omitted from the
 *   inherited attribute set.
 */
import * as React from 'react';

export type WindowBackground = 'paper' | 'white' | 'yellow' | 'mint' | 'pink' | 'blue';
export type WindowShadow = 's' | 'm' | 'l' | 'xl';

export interface WindowProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Mono filename shown at the right of the titlebar. @default "WINDOW.EXE" */
  title?: React.ReactNode;
  children?: React.ReactNode;
  /** Shell fill (visible around the body). @default "paper" */
  background?: WindowBackground;
  /** Body fill. @default "white" */
  bodyBackground?: WindowBackground;
  /** Hard offset shadow size. @default "l" */
  shadow?: WindowShadow;
  /** Applied to the outer shell. */
  style?: React.CSSProperties;
  /** Applied to the body panel, after its background + padding. */
  bodyStyle?: React.CSSProperties;
}

const BACKGROUNDS: Record<WindowBackground, string> = {
  paper: 'var(--lq-paper)',
  white: 'var(--lq-white)',
  yellow: 'var(--lq-yellow)',
  mint: 'var(--lq-mint)',
  pink: 'var(--lq-pink)',
  blue: 'var(--lq-blue)',
};

// NOTE: Window's background map omits `purple` and `ink`, unlike Card's. Matches the manifest.
const SHADOWS: Record<WindowShadow, string> = {
  s: 'var(--lq-shadow-s)',
  m: 'var(--lq-shadow-m)',
  l: 'var(--lq-shadow-l)',
  xl: 'var(--lq-shadow-xl)',
};

const DOT_COLORS = ['var(--lq-red)', 'var(--lq-yellow)', 'var(--lq-mint)'] as const;

// 12px dot, 50% radius. NOTE: 50% rather than var(--lq-radius-full) (999px) — identical on a
// square, kept literal to match the bundle byte-for-byte.
const dotStyle = (background: string): React.CSSProperties => ({
  width: 12,
  height: 12,
  borderRadius: '50%',
  border: 'var(--lq-border)',
  background,
});

export function Window({
  title = 'WINDOW.EXE',
  children,
  background = 'paper',
  bodyBackground = 'white',
  shadow = 'l',
  style,
  bodyStyle,
  ...rest
}: WindowProps) {
  return (
    <div
      {...rest}
      style={{
        border: 'var(--lq-border)',
        background: BACKGROUNDS[background],
        boxShadow: SHADOWS[shadow],
        fontFamily: 'var(--lq-font-sans)',
        ...style,
      }}
    >
      {/* NOTE: the bundle gives Window no hover/press behaviour at all, despite the readme's
          "hover lifts the object" rule for surfaces. Reproduced as shipped — static. */}
      <div
        style={{
          minHeight: 44,
          borderBottom: 'var(--lq-border)',
          display: 'flex',
          alignItems: 'center',
          padding: '0 14px',
          gap: 8,
          background: 'var(--lq-white)',
        }}
      >
        {DOT_COLORS.map((color) => (
          <span key={color} style={dotStyle(color)} />
        ))}
        <b
          style={{
            marginLeft: 'auto',
            fontFamily: 'var(--lq-font-mono)',
            fontSize: 11,
            // NOTE: 0.06em, not the --lq-label-tracking token (0.08em) the readme prescribes
            // for mono labels. Bundle wins.
            letterSpacing: '0.06em',
          }}
        >
          {title}
        </b>
      </div>
      <div
        style={{
          background: BACKGROUNDS[bodyBackground],
          padding: 24,
          ...bodyStyle,
        }}
      >
        {children}
      </div>
    </div>
  );
}

export default Window;
